import { API_METHODS } from '@compass/client/constants/api-methods';
import { workflowService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useQuery } from '@tanstack/react-query';

import { WorkflowType } from '@/constants/dispositions';

import { usePermissionMap } from './usePermissions';

/**
 * Permission-map key for the program Cases list. Program-level methods are
 * evaluated against the program, so the server either reports them (`true` /
 * `false`) or leaves them out. It leaves a method out only when the user's
 * access to it is limited to their own records (CheckMethodPermissions skips
 * level-0 matches such as `case_record:read:assigned`).
 */
const CASE_LIST_METHOD = API_METHODS.CaseRecordsService.FindCaseRecords;

/**
 * Whether the permission map says the user can see only the cases assigned to
 * them (the Case Manager role). An unknown map (still loading, or failed) is
 * not treated as assigned-only, so those users keep the Cases landing.
 */
export function isAssignedOnlyCaseAccess(
  permissions: Record<string, boolean> | undefined,
): boolean {
  return !!permissions && !(CASE_LIST_METHOD in permissions);
}

/**
 * Whether the program offers My Cases: not a PQC program, the same rule as
 * useShouldShowMyCasesPage. Unknown workflows (the query failed) count as not
 * offered, so those users keep the Cases landing.
 */
function useProgramOffersMyCases(
  organizationId: string | null | undefined,
  programId: string | null | undefined,
): { isLoading: boolean; offersMyCases: boolean } {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.workflows.byProgram(programId || ''),
    queryFn: () =>
      workflowService.getWorkflowsByProgram({
        organization_id: organizationId!,
        program_id: programId!,
      }),
    enabled: !!organizationId && !!programId,
  });
  const isPqc = data?.workflows?.some(workflow => workflow.workflow_type === WorkflowType.PQC);
  return { isLoading, offersMyCases: !isError && !isPqc };
}

interface MyCasesLanding {
  /** True while the landing can't be decided yet; callers should not navigate. */
  isResolving: boolean;
  /** True when the user should land on My Cases instead of the Cases list. */
  landOnMyCases: boolean;
}

/**
 * Decides whether the default landing (after login, or on `/` and `/org-{id}`)
 * is My Cases instead of the program Cases list (BV-2347): a user whose case
 * access is assigned-only lands on the cases assigned to them. Every other
 * user, and every PQC program (where My Cases is not offered), keeps the
 * Cases landing.
 *
 * @param organizationId - Organization the user is landing in
 * @param programId - Program whose permissions decide the landing
 */
export function useMyCasesLanding(
  organizationId: string | null | undefined,
  programId: string | null | undefined,
): MyCasesLanding {
  const hasContext = !!organizationId && !!programId;

  const workflows = useProgramOffersMyCases(organizationId, programId);

  // Without a program the map can't answer for program-level methods, so don't
  // fetch it (a missing organization disables the query).
  const { data, isLoading } = usePermissionMap({
    organizationId: hasContext ? organizationId : null,
    programId: hasContext ? programId : null,
  });

  if (!hasContext || isLoading || workflows.isLoading) {
    return { isResolving: true, landOnMyCases: false };
  }
  return {
    isResolving: false,
    landOnMyCases: workflows.offersMyCases && isAssignedOnlyCaseAccess(data?.permissions),
  };
}
